import { Button } from '@mantine/core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMyFundCallLines } from 'src/Hooks/Queries/useFundCalls';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import styles from './FundCallBanner.module.scss';

/**
 * Bandeau « vous avez quelque chose à verser ».
 *
 * Ne rend rien quand il n'y a rien à verser : un bandeau permanent finit par ne plus être lu.
 */
const FundCallBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: fundCalls } = useMyFundCallLines();
  const assetId = useCurrentAssetId();

  const lines = (fundCalls ?? []).flatMap(fundCall =>
    fundCall.lines.map(line => ({ line, fundCall }))
  );
  if (lines.length === 0) return null;

  const total = lines.reduce((sum, { line }) => sum + line.amountCalled - line.amountPaid, 0);
  // Le client génère les dates en Dayjs : on trie sur elles directement.
  const soonest = lines
    .map(({ fundCall }) => fundCall.dueDate)
    .filter((date): date is NonNullable<typeof date> => !!date)
    .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())[0];

  return (
    <div className={styles.container}>
      <div className={styles.text}>
        {soonest
          ? t('fundCalls.banner.withDate', '{{amount}} € à verser avant le {{date}}', {
              amount: total.toFixed(2),
              date: dayjs(soonest).format('DD/MM'),
            })
          : t('fundCalls.banner.withoutDate', '{{amount}} € à verser', {
              amount: total.toFixed(2),
            })}
      </div>
      <Button size="xs" onClick={() => navigate(`/asset/${assetId}/expenses/my-statement`)}>
        {t('fundCalls.banner.see', 'Voir mon relevé')}
      </Button>
    </div>
  );
};

export default FundCallBanner;
